"use client";

import { useCallback, useState } from "react";

import { addToCart, type AddToCartActionRequest } from "@/app/actions/cart";
import { useCartStore, useSessionStore } from "@/app/store";

/**
 * Custom hook to handle adding a product to the cart.
 * Reads and updates cart state via the Zustand store, and delegates
 * business logic to the addToCart server action.
 */
export function useAddToCart(payload: AddToCartActionRequest): {
	cartQuantity: number;
	handleClick: () => Promise<void>;
	loading: boolean;
	error: string | null;
	submitted: boolean;
} {
	const sessionState = useSessionStore((state) => state.sessionState);
	const cartState = useCartStore((state) => state.cartState);
	const setCartState = useCartStore((state) => state.setCartState);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [submitted, setSubmitted] = useState(false);

	const cartQuantity =
		cartState.status === "connected"
			? cartState.entries.reduce((acc, entry) => acc + entry.quantity, 0)
			: 0;

	const handleClick = useCallback(async () => {
		if (!sessionState) {
			setError("No active session");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await addToCart(sessionState, payload);

			if (response.status === "failed") {
				setError(response.message);
				return;
			}

			setSubmitted(true);
			setCartState(response.cart);

			setTimeout(() => setSubmitted(false), 1000);
		} catch (err) {
			if (err instanceof Error) {
				setError(err.message);
			}
		} finally {
			setLoading(false);
		}
	}, [payload, sessionState, setCartState]);

	return { cartQuantity, handleClick, loading, error, submitted };
}
